import React, { useState, useEffect, useMemo } from 'react';
import clsx from 'clsx';
// parent Overview will provide data via props
import { Grid, Typography, Card, CardHeader, Divider, List, ListItem, ListItemText, Box, Avatar, useTheme, useMediaQuery, IconButton } from '@material-ui/core';
import BarChartIcon from '@material-ui/icons/BarChart';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import {
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  makeStyles
} from '@material-ui/core';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import { BarChart } from 'recharts';

const useStyles = makeStyles(() => ({
  root: {
    borderRadius: 16,
    boxShadow: "0 2px 12px rgba(0,45,85,0.08)",
    background: '#f7faff',
    //backgroundColor: "#FFFFFF",
    marginBottom: 24,
    padding: 0,
  },
  actions: {
    justifyContent: 'flex-end'
  },
  tabRoot: {
    color: "#FFFFFF",
    backgroundColor: '#00e676'
  },
  cardHeaderRoot: {
    alignItems: 'center',
  },
  avatar: {
    backgroundColor: '#6a8caf',
    height: 'clamp(20px, 2.2vw, 36px)',
    width: 'clamp(20px, 2.2vw, 36px)',
  },
  cardHeaderTitle: {
    color: "#002D55",
    fontWeight: "bold",
    fontSize: 'clamp(15px, 1.4vw, 22px)',
  },
  // responsive typography for session content
  sessionPhase: {
    color: '#002D55',
    fontWeight: 'bold',
    marginBottom: 6,
    fontSize: 'clamp(13px, 1vw, 16px)',
  },
  sessionTime: {
    color: '#486d86',
    marginBottom: 10,
    fontSize: 'clamp(11px, 0.9vw, 14px)',
  },
  exerciseTitle: {
    fontSize: 'clamp(12px, 0.95vw, 15px)',
    fontWeight: 'bold',
    color: '#002D55',
  },
  metricLabel: {
    color: '#002D55',
    fontWeight: 'normal',
    fontSize: 'clamp(12px, 0.95vw, 15px)',
  },
  feedbackTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#002D55',
    fontWeight: 'bold',
    fontSize: 'clamp(11px, 0.9vw, 14px)',
  },
  feedbackText: {
    color: '#333',
    fontSize: 'clamp(10px, 0.82vw, 13px)',
  },
  metaDate: {
    fontWeight: 'bold',
    color: '#002D55',
    fontSize: 'clamp(13px, 1vw, 16px)',
    textAlign: 'center',
  },
  metaExplainer: {
    color: '#486d86',
    marginTop: 8,
    fontSize: 'clamp(10px, 0.82vw, 13px)',
    textAlign: 'center',
  },
  ringInner: {
    fontWeight: 800,
    color: '#002D55',
    fontSize: 'clamp(9px, 0.75vw, 12px)',
  }
  ,
  // responsive table cell widths
  tableDayCell: {
    width: 160,
    ['@media (max-width:600px)']: {
      width: 120,
    },
    ['@media (min-width:600px)']: {
      width: 180,
    },
    ['@media (min-width:960px)']: {
      width: 220,
    },
    ['@media (min-width:1280px)']: {
      width: 260,
    }
  },
  tableSessionsCell: {
    // allow this column to grow on larger screens by setting minWidth at breakpoints
    minWidth: 200,
    ['@media (min-width:600px)']: {
      minWidth: 360,
    },
    ['@media (min-width:960px)']: {
      minWidth: 520,
    },
    ['@media (min-width:1280px)']: {
      minWidth: 720,
    }
  }
  ,
  // responsive chip styles
  chipBase: {
    fontSize: 'clamp(9px, 0.75vw, 12px)',
    marginTop: 1,
  },
  chipSmall: {
    height: 18,
    lineHeight: '18px',
    padding: '0 2px'
  },
  chipMedium: {
    height: 22,
    lineHeight: '22px',
    padding: '0 4px'
  },
  chipTime: {
    fontWeight: 'bold'
  }
}));

const PatientJourney = ({ className, completedSessionsList: propCompletedSessionsList = [], progress: propProgress = 0, title: propTitle = '', ...rest }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  // also detect exactly 'sm' so we can toggle compact UI on xs or sm
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));
  // NOTE: The true planned frequency is defined by the treatment program data on the backend
  // (see `backend/DjangoRestEks/data/defaultExersicePrograms.json`). That file contains
  // per-diagnosis / per-phase exercise `frequency` entries which should be used to
  // compute the planned sessions per day for a user. `PLANNED_SESSIONS_PER_DAY` is a
  // temporary fallback used for the overview UI when the plan-derived value is not
  // available client-side. Prefer props passed from parent (`planned_sessions_per_day_overall`)
  // or camelCase `plannedSessionsPerDay` when available.
  const PLANNED_SESSIONS_PER_DAY = 3; // fallback default (matches ChartProgress assumption)
  const plannedFromProps = (rest && (rest.planned_sessions_per_day_overall ?? rest.plannedSessionsPerDay ?? rest.plannedSessionsPerDayOverall)) || null;
  const plannedSessionsPerDay = (Number.isFinite(Number(plannedFromProps)) && Number(plannedFromProps) > 0) ? Number(plannedFromProps) : PLANNED_SESSIONS_PER_DAY;

  // Group sessions by day (YYYY-MM-DD)
  const groupedByDay = useMemo(() => {
    const known = {};
    const unknown = [];
    (propCompletedSessionsList || []).forEach(s => {
      if (s.completed_date) {
        const day = moment(s.completed_date).format('YYYY-MM-DD');
        if (!known[day]) known[day] = [];
        known[day].push(s);
      } else {
        unknown.push(s);
      }
    });

    // sort known days (newest first) and sort sessions inside each day newest-first
    const days = Object.keys(known)
      .sort((a, b) => b.localeCompare(a))
      .map(day => ({
        day,
        sessions: (known[day] || []).slice().sort((x, y) => {
          const tx = x.completed_date ? moment(x.completed_date).valueOf() : 0;
          const ty = y.completed_date ? moment(y.completed_date).valueOf() : 0;
          return ty - tx;
        })
      }));

    // append unknown-date sessions at the end (so latest-known dates are shown first)
    if (unknown.length) {
      days.push({ day: 'unknown', sessions: unknown });
    }
    return days;
  }, [propCompletedSessionsList]);

  const compactPageSize = isXs ? 1 : isSm ? 2 : isMd ? 3 : null;

  const getLastPageStart = (length, pageSize) => {
    if (!pageSize || length <= pageSize) return 0;
    return Math.floor((length - 1) / pageSize) * pageSize;
  };

  // Visible session start index per day for xs/sm/md paging.
  const [visibleIndexByDay, setVisibleIndexByDay] = useState({});
  const showPrev = (day) => {
    setVisibleIndexByDay(prev => {
      const len = (groupedByDay.find(g => g.day === day)?.sessions || []).length || 1;
      const cur = prev[day] || 0;
      if (!compactPageSize || len <= compactPageSize) {
        return { ...prev, [day]: 0 };
      }
      const nextStart = cur - compactPageSize;
      return { ...prev, [day]: nextStart >= 0 ? nextStart : getLastPageStart(len, compactPageSize) };
    });
  };
  const showNext = (day) => {
    setVisibleIndexByDay(prev => {
      const len = (groupedByDay.find(g => g.day === day)?.sessions || []).length || 1;
      const cur = prev[day] || 0;
      if (!compactPageSize || len <= compactPageSize) {
        return { ...prev, [day]: 0 };
      }
      const nextStart = cur + compactPageSize;
      return { ...prev, [day]: nextStart < len ? nextStart : 0 };
    });
  };

  const sessionCardStyle = {
    minWidth: 320,
    flex: '0 0 320px',
    background: 'linear-gradient(90deg, #f5faff 60%, #e9f1f6)',
    padding: 12,
    borderRadius: 8,
    boxShadow: '0 1px 4px rgba(0,45,85,0.04)'
  };

  // Render a single session card (header, exercises, feedback)
  const renderSessionCard = (session) => {
    if (!session) return null;
    const phaseName = String(session.phase || '')
      .replace(/^Phase\s+\d+\s*:\s*['"]?/, '')
      .replace(/['"]?\.?\s*$/, '');
    return (
      <div>
        <div className={classes.sessionPhase}>{phaseName ? `Phase ${session.phase_index !== undefined ? session.phase_index + 1 : ''}: '${phaseName}'.` : ''}</div>
        <div className={classes.sessionTime}>{session.completed_date ? `Patient completed exercises at: 📅 ${moment(session.completed_date).format('MMM D, YYYY [at] HH:mm')}` : 'Patient completed exercises at: —'}</div>

        
          {session.exercise_feedback && session.exercise_feedback.length > 0 ? (
            session.exercise_feedback.map((ex, exIdx) => (
              <div key={ex.id || exIdx} style={{ marginBottom: 10 }}>
                  {/* Exercise title as text (keep original patient view) */}
                  <div className={classes.exerciseTitle} style={{ display: 'inline-block', marginRight: 8 }}>{ex.exercise_title ? ex.exercise_title : (ex.exercise_id || '-')}</div>
                  {ex.note && ex.note.trim() !== "" ? (
                    <span style={{ marginLeft: 8, color: '#002D55' }}>{ex.note}</span>
                  ) : null}
                  {/* Pain and Difficulty below title, on one line with | (exact format from SelectedPatientJourney.js) */}
                  {(ex.painLevel || ex.difficulty) && (
                    <div style={{ marginTop: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ display: 'flex', alignItems: 'center', marginRight: 0 }}>
                        <span className={classes.metricLabel} style={{ marginRight: 4, paddingLeft: 10 }}>Pain:</span>
                        <Chip
                          className={clsx(classes.chipBase, classes.chipSmall)}
                          label={ex.painLevel ?? "-"}
                          style={{
                            marginRight: 8, marginBottom: 2,
                            backgroundColor:
                              ex.painLevel === undefined || ex.painLevel === null ? 'rgba(255,243,224,0.75)'
                              : ex.painLevel < 35 ? 'rgba(67,234,122,0.75)'
                              : ex.painLevel >= 35 && ex.painLevel <= 49 ? 'rgba(255,224,102,0.75)'
                              : ex.painLevel >= 50 && ex.painLevel <= 60 ? 'rgba(255,179,71,0.75)'
                              : ex.painLevel > 70 ? 'rgba(255,77,79,0.75)'
                              : 'rgba(255,243,224,0.75)',
                            color: '#002D55',
                            fontWeight: 'bold'
                          }}
                          size="small"
                        />
                      </span>
                      <span style={{ color: '#002D55', fontWeight: 'bold', margin: '0 8px' }}>|</span>
                      <span style={{ display: 'flex', alignItems: 'center', marginRight: 12 }}>
                        <span className={classes.metricLabel} style={{ marginRight: 4 }}>Difficulty:</span>
                        { (parseFloat(ex.difficulty) < 33.33) || ex.difficulty === 'Good.' ? (
                          <Chip className={clsx(classes.chipBase, classes.chipMedium)} icon={<CheckCircleIcon style={{  marginTop: -3, color: '#43ea7a', fontSize: 18 }} />} label={'Good.'} size="small" style={{ marginTop: 0, backgroundColor: 'transparent', color: '#002D55', fontWeight: 'bold' }} />
                        ) : (
                          <Chip className={clsx(classes.chipBase, classes.chipMedium)} label={
                            parseFloat(ex.difficulty) === 33.33 ? 'Too easy.'
                            : parseFloat(ex.difficulty) === 66.66 ? 'Too hard.'
                            : parseFloat(ex.difficulty) === 100 ? 'Lack of equipment.'
                            : (ex.difficulty ?? '-')
                          } size="small" style={{ marginTop: 0, backgroundColor: 'rgba(255,179,71,0.75)', color: '#002D55', fontWeight: 'bold' }} />
                        )}
                      </span>
                    </div>
                  )}
                </div>
            ))
          ) : (
            <div style={{ color: '#486d86' }}>No exercises recorded.</div>
          )}

          <div style={{ marginTop: 18 }}>
          <div className={classes.feedbackTitle}>
            <span role="img" aria-label="feedback">💬</span>
            <span>Patient feedback on session</span>
          </div>
          <div style={{ marginTop: 8 }}>
            {session.session_feedback && session.session_feedback.length > 0 ? (
              session.session_feedback.map((sf, sfIdx) => (
                  <div key={sf.id || sfIdx} style={{ marginBottom: 8 }}>
                  {sf.like_status ? <Chip className={clsx(classes.chipBase, classes.chipSmall)} label={sf.like_status === 'like' ? '👍 Liked' : sf.like_status} style={{ marginRight: 8, backgroundColor: '#e0f7fa', color: '#002D55' }} size="small" /> : null}
                  <span className={classes.feedbackText}>{sf.feedback && sf.feedback.trim() !== '' ? sf.feedback : 'No additional feedback provided.'}</span>
                </div>
              ))
            ) : (
              <div style={{ color: '#486d86' }}>No session feedback.</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Count completed sessions for a grouped day using available backend feedback
  const countCompletedForGroup = (group) => {
    if (!group || !group.sessions) return 0;
    const completed = group.sessions.filter(s => {
      const hasExerciseFeedback = s.exercise_feedback && s.exercise_feedback.length > 0;
      const hasSessionFeedback = s.session_feedback && s.session_feedback.length > 0;
      const hasCompletedDate = !!s.completed_date;
      return hasExerciseFeedback || hasSessionFeedback || hasCompletedDate;
    }).length;
    return completed;
  };

  // Small helper to render a circular ring used as a compact legend (copied from ChartProgress.js)
  const renderRing = (color, value, size = 48, labelInside, emptyColor = 'rgba(89, 93, 89, 0.18)') => {
    const numeric = (value === null || value === undefined) ? NaN : Number(value);
    const valid = Number.isFinite(numeric);
    const deg = valid ? Math.max(0, Math.min(360, numeric * 3.6)) : 0;
    const gradient = `conic-gradient(${emptyColor} 0deg ${360 - deg}deg, ${color} ${360 - deg}deg 360deg)`;
    return (
      <Box style={{
        width: size,
        height: size,
        borderRadius: '50%',
        padding: 6,
        background: 'rgba(255,255,255,0.95)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Box style={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: gradient
        }}>
          <Box style={{
            width: '69%',
            height: '69%',
            borderRadius: '50%',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div className={classes.ringInner}>{(labelInside !== undefined && labelInside !== null) ? labelInside : (valid ? `${Math.round(numeric)}` : '--')}</div>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Card
      className={clsx(classes.root, className)}
      {...rest}
    >
      <CardHeader
        avatar={
          <Avatar className={classes.avatar}>
            <BarChartIcon style={{ color: '#fff', fontSize: 'clamp(14px, 1.4vw, 22px)' }} />
          </Avatar>
        }
  title={<span className={classes.cardHeaderTitle}>Activity log</span>}
        className={classes.cardHeaderRoot}
        style={{ marginBottom: 5, padding: 10, borderBottom: "2px solid #fb741a", alignItems: 'center' }}
      />
      <Box minWidth={400} style={{paddingLeft: 12, paddingRight: 12, width: "100%", marginLeft: 0}}>
        <Typography style={{ color: "#002D55", fontWeight: "normal", fontSize: 14, marginTop: 8, marginBottom: 8, paddingLeft: 15,  }}>
          Here you can get an overview of your rehabilitation sessions, symptomresponse and feedback.
        </Typography>
        {/* Grouped by-day view: left meta column with counts, right side sessions grouped horizontally */}
        <Table style={{ marginBottom: 14, borderCollapse: 'collapse', width: '100%', borderRadius: 16, overflow: 'hidden', backgroundColor: "#FFF", boxShadow: '0 2px 8px rgba(0,45,85,0.18)' }}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableDayCell} style={{ fontWeight: 'bold', color: '#002D55', borderBottom: '2px solid #e0e0e0' }}>Day</TableCell>
              <TableCell className={classes.tableSessionsCell} style={{ fontWeight: 'bold', color: '#002D55', borderBottom: '2px solid #e0e0e0' }}>Sessions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedByDay && groupedByDay.length > 0 ? (
              groupedByDay.map((group) => (
                <TableRow key={group.day} style={{ backgroundColor: '#FFF' }}>
                  <TableCell className={classes.tableDayCell} style={{ verticalAlign: 'top', padding: 12, border: '1px solid #e8eef6', background: '#fbfbfc' }}>
                    {(() => {
                      const completed = countCompletedForGroup(group);
                      const planned = plannedSessionsPerDay;
                      const pct = Math.round((completed / Math.max(1, planned)) * 100);
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                          <div className={classes.metaDate}>{group.day === 'unknown' ? 'Unknown' : moment(group.day).format('MMM D, YYYY')}</div>
                          {renderRing('#fb741a', pct, 64, `${completed}/${planned}`)}
                          <div className={classes.metaExplainer}>You completed {completed}/{planned} planned sessions at:</div>
                          {/* session times: stacked vertically below date and above explainer text */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6, alignItems: 'center' }}>
                            {group.sessions && group.sessions.length > 0 ? (
                              group.sessions.map((s, i) => (
                                <Chip key={s.id || i} className={clsx(classes.chipBase, classes.chipTime)} label={s.completed_date ? moment(s.completed_date).format('- HH:mm') : '—'} size="small" style={{ background: '#fff', color: '#002D55' }} />
                              ))
                            ) : null}
                          </div>
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className={classes.tableSessionsCell} style={{ verticalAlign: 'top', padding: 12, border: '1px solid #e8eef6' }}>
                    {(() => {
                      const pageSize = compactPageSize;
                      const totalSessions = group.sessions.length;
                      const startIndex = visibleIndexByDay[group.day] || 0;
                      const visibleSessions = pageSize
                        ? group.sessions.slice(startIndex, startIndex + pageSize)
                        : group.sessions;
                      const endIndex = Math.min(startIndex + (pageSize || totalSessions), totalSessions);
                      const shouldPage = Boolean(pageSize);
                      const showPager = shouldPage && totalSessions > pageSize;

                      return shouldPage ? (
                      <div>
                        {showPager ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                            <IconButton size="small" onClick={() => showPrev(group.day)}><ChevronLeftIcon /></IconButton>
                            <div style={{ flex: 1, textAlign: 'center', color: '#486d86' }}>{`${startIndex + 1}-${endIndex} / ${totalSessions}`}</div>
                            <IconButton size="small" onClick={() => showNext(group.day)}><ChevronRightIcon /></IconButton>
                          </div>
                        ) : null}
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'nowrap', overflow: 'hidden' }}>
                          {visibleSessions.map((session) => (
                            <div key={session.id || uuid()} style={{ ...sessionCardStyle, minWidth: 0, flex: `1 1 ${100 / (pageSize || 1)}%` }}>
                              {renderSessionCard(session)}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 6 }}>
                        {group.sessions.map((session) => (
                          <div key={session.id || uuid()} style={sessionCardStyle}>
                            {renderSessionCard(session)}
                          </div>
                        ))}
                      </div>
                    );
                    })()}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2}>No sessions completed yet.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {/* You can add a progress bar here if needed, e.g. <DiscreteSlider value={progress} /> */}
      </Box>
      <Box
        display="flex"
        justifyContent="flex-end"
        p={2}
      >
        <Button
          color="primary"
          endIcon={<ArrowRightIcon />}
          size="small"
          variant="text"
          style={{ fontWeight: "bold", color: "#002D55" }}
        >
          View all
        </Button>
      </Box>
    </Card>
  );
};

PatientJourney.propTypes = {
  className: PropTypes.string,
  // Optional planned sessions per day injected from parent (backend-provided)
  plannedSessionsPerDay: PropTypes.number,
  planned_sessions_per_day_overall: PropTypes.number
};

export default PatientJourney;
